import React, { useCallback, useEffect, useRef, useState } from "react";
import { notification } from "antd";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import { IoCubeOutline, IoGiftOutline, IoPlayOutline, IoRocketOutline } from "react-icons/io5";
import { normalizeLanguage } from "../../services/language";
import { useInterfaceLanguage } from "../../hooks/useInterfaceLanguage";

const SANDBOX_COPY = Object.freeze({
  backToLobby: {
    es: "Volver al lobby",
    en: "Back to lobby",
    de: "Zurück zur Lobby",
    ru: "Назад в лобби",
  },
  title: {
    es: "Caja de arena",
    en: "Sandbox",
    de: "Sandbox",
    ru: "Песочница",
  },
  subtitle: {
    es: "Elige modo y jugadores para practicar sin límites.",
    en: "Choose a mode and number of players to practice without limits.",
    de: "Wähle einen Modus und die Spielerzahl, um ohne Einschränkungen zu üben.",
    ru: "Выбери режим и количество игроков, чтобы тренироваться без ограничений.",
  },
  scienceModeTitle: {
    es: "Modo Ciencia",
    en: "Science mode",
    de: "Wissenschaftsmodus",
    ru: "Режим «Наука»",
  },
  scienceModeSubtitle: {
    es: "Muévete con total libertad.",
    en: "Move with complete freedom.",
    de: "Bewege dich völlig frei.",
    ru: "Перемещайся совершенно свободно.",
  },
  scienceModeDetail: {
    es: "Experimenta nuevas estrategias.",
    en: "Try new strategies.",
    de: "Probiere neue Strategien aus.",
    ru: "Пробуй новые стратегии.",
  },
  chanceModeTitle: {
    es: "Modo Azar",
    en: "Chance mode",
    de: "Zufallsmodus",
    ru: "Режим случайности",
  },
  chanceModeSubtitle: {
    es: "Prueba la suerte sin límites.",
    en: "Test your luck without limits.",
    de: "Teste dein Glück ohne Grenzen.",
    ru: "Испытай удачу без ограничений.",
  },
  chanceModeDetail: {
    es: "Crea situaciones imposibles.",
    en: "Create impossible scenarios.",
    de: "Erschaffe unmögliche Situationen.",
    ru: "Создавай невозможные ситуации.",
  },
  cienzarModeTitle: {
    es: "Modo CIENZAR",
    en: "CIENZAR mode",
    de: "CIENZAR-Modus",
    ru: "Режим CIENZAR",
  },
  cienzarModeSubtitle: {
    es: "Combina ciencia y azar.",
    en: "Combine science and chance.",
    de: "Verbinde Wissenschaft und Zufall.",
    ru: "Сочетай науку и случайность.",
  },
  cienzarModeDetail: {
    es: "Rompe las reglas y descubre nuevas jugadas.",
    en: "Break the rules and discover new moves.",
    de: "Brich die Regeln und entdecke neue Spielzüge.",
    ru: "Нарушай правила и открывай новые ходы.",
  },
  scienceVsAzarModeTitle: {
    es: "Modo Ciencia V/S Azar",
    en: "Science V/S Chance mode",
    de: "Wissenschaft V/S Zufall-Modus",
    ru: "Science V/S Chance mode",
  },
  scienceVsAzarModeSubtitle: {
    es: "Prueba roles fijos.",
    en: "Test fixed roles.",
    de: "Teste feste Rollen.",
    ru: "Test fixed roles.",
  },
  scienceVsAzarModeDetail: {
    es: "Ciencia sin dados contra azar obligado a lanzar.",
    en: "Science without dice against chance forced to roll.",
    de: "Wissenschaft ohne Wuerfel gegen Zufall mit Wuerfelpflicht.",
    ru: "Science without dice against chance forced to roll.",
  },
  playersTitle: {
    es: "Jugadores",
    en: "Players",
    de: "Spieler",
    ru: "Игроки",
  },
  playersDescription: {
    es: "Elige el número de jugadores en la caja de arena.",
    en: "Choose the number of players in the sandbox.",
    de: "Wähle die Anzahl der Spieler in der Sandbox.",
    ru: "Выбери количество игроков в песочнице.",
  },
  oneVsOne: {
    es: "1 vs 1",
    en: "1 vs 1",
    de: "1 gegen 1",
    ru: "1 на 1",
  },
  oneVsTwo: {
    es: "1 vs 2",
    en: "1 vs 2",
    de: "1 gegen 2",
    ru: "1 на 2",
  },
  oneVsThree: {
    es: "1 vs 3",
    en: "1 vs 3",
    de: "1 gegen 3",
    ru: "1 на 3",
  },
  twoPlayers: {
    es: "Dos jugadores",
    en: "Two players",
    de: "Zwei Spieler",
    ru: "Два игрока",
  },
  threePlayers: {
    es: "Tres jugadores",
    en: "Three players",
    de: "Drei Spieler",
    ru: "Три игрока",
  },
  fourPlayers: {
    es: "Cuatro jugadores",
    en: "Four players",
    de: "Vier Spieler",
    ru: "Четыре игрока",
  },
  rulesTitle: {
    es: "Reglas libres",
    en: "Freeform rules",
    de: "Freie Regeln",
    ru: "Свободные правила",
  },
  rulesIntro: {
    es: "Entrarás en el modo Caja de Arena actual.",
    en: "You will enter the current Sandbox mode.",
    de: "Du gelangst in den aktuellen Sandbox-Modus.",
    ru: "Ты перейдёшь в текущий режим «Песочница».",
  },
  rulesAriaLabel: {
    es: "Funciones de caja de arena",
    en: "Sandbox features",
    de: "Sandbox-Funktionen",
    ru: "Функции песочницы",
  },
  ruleFreeMovement: {
    es: "Movimiento libre con reglas especiales activadas.",
    en: "Free movement with special rules enabled.",
    de: "Freie Bewegung mit aktivierten Sonderregeln.",
    ru: "Свободное перемещение с включёнными особыми правилами.",
  },
  ruleRightClick: {
    es: "Compatible con el clic derecho y la lógica actual de FreeBox.",
    en: "Compatible with right-click and the current FreeBox logic.",
    de: "Kompatibel mit Rechtsklick und der aktuellen FreeBox-Logik.",
    ru: "Совместимо с правой кнопкой мыши и текущей логикой FreeBox.",
  },
  ruleNoTurnTime: {
    es: "Sin selector de tiempo por turno, igual que la caja original.",
    en: "No per-turn time selector, just like the original sandbox.",
    de: "Keine Zeitauswahl pro Zug, genau wie in der ursprünglichen Sandbox.",
    ru: "Без выбора времени на ход, как в оригинальной песочнице.",
  },
  goToShop: {
    es: "Ir a la tienda",
    en: "Go to shop",
    de: "Zum Shop",
    ru: "В магазин",
  },
  shopDescription: {
    es: "Consigue nuevos estilos para tus partidas.",
    en: "Get new styles for your matches.",
    de: "Hol dir neue Designs für deine Partien.",
    ru: "Получи новые стили для своих партий.",
  },
  play: {
    es: "Jugar",
    en: "Play",
    de: "Spielen",
    ru: "Играть",
  },
  opening: {
    es: "Abriendo...",
    en: "Opening...",
    de: "Wird geöffnet...",
    ru: "Открытие...",
  },
  championshipTitle: {
    es: "Campeonato Mundial XIMA 2026",
    en: "XIMA World Championship 2026",
    de: "XIMA-Weltmeisterschaft 2026",
    ru: "Чемпионат мира по XIMA 2026",
  },
  championshipDescription: {
    es: "Practica libremente y prepara tus mejores jugadas.",
    en: "Practice freely and prepare your best moves.",
    de: "Übe frei und bereite deine besten Spielzüge vor.",
    ru: "Тренируйся свободно и готовь свои лучшие ходы.",
  },
  modeSectionLabel: {
    es: "Modo de juego caja de arena",
    en: "Sandbox game mode",
    de: "Sandbox-Spielmodus",
    ru: "Режим игры в песочнице",
  },
  missingSandboxId: {
    es: "No se recibió el identificador de la caja de arena.",
    en: "The sandbox identifier was not received.",
    de: "Die Sandbox-Kennung wurde nicht empfangen.",
    ru: "Идентификатор песочницы не был получен.",
  },
  startErrorTitle: {
    es: "No se pudo iniciar la caja de arena",
    en: "The sandbox could not be started",
    de: "Die Sandbox konnte nicht gestartet werden",
    ru: "Не удалось запустить песочницу",
  },
  startErrorDescription: {
    es: "Intenta iniciar la caja de arena nuevamente.",
    en: "Try starting the sandbox again.",
    de: "Versuche erneut, die Sandbox zu starten.",
    ru: "Попробуй запустить песочницу ещё раз.",
  },
});

const SANDBOX_GAME_MODE_OPTIONS = [
  {
    id: "free",
    titleKey: "scienceModeTitle",
    subtitleKey: "scienceModeSubtitle",
    detailKey: "scienceModeDetail",
    icon: "/mode-icons/free.png",
    className: "xima-create-mode-science",
  },
  {
    id: "dice",
    titleKey: "chanceModeTitle",
    subtitleKey: "chanceModeSubtitle",
    detailKey: "chanceModeDetail",
    icon: "/mode-icons/dice.png",
    className: "xima-create-mode-luck",
  },
  {
    id: "cienzar",
    titleKey: "cienzarModeTitle",
    subtitleKey: "cienzarModeSubtitle",
    detailKey: "cienzarModeDetail",
    icon: "/mode-icons/cienzar.png",
    className: "xima-create-mode-cienzar",
  },
  {
    id: "sciencevsazar",
    titleKey: "scienceVsAzarModeTitle",
    subtitleKey: "scienceVsAzarModeSubtitle",
    detailKey: "scienceVsAzarModeDetail",
    icon: "/mode-icons/cienzar.png",
    className: "xima-create-mode-sciencevsazar",
  },
];

const SANDBOX_PLAYER_OPTIONS = [
  {
    value: 2,
    labelKey: "oneVsOne",
    detailKey: "twoPlayers",
    icon: "/mode-icons/players-1v1.png",
  },
  {
    value: 3,
    labelKey: "oneVsTwo",
    detailKey: "threePlayers",
    icon: "/mode-icons/players-1v2.png",
  },
  {
    value: 4,
    labelKey: "oneVsThree",
    detailKey: "fourPlayers",
    icon: "/mode-icons/players-1v3.png",
  },
];

const SANDBOX_DEFAULT_TURN_TIME = 5;
const PLAY_SANDBOX_PREFERENCES_STORAGE_KEY = "xima_play_sandbox_preferences_v1";
const PLAY_SANDBOX_DEFAULT_PREFERENCES = {
  mode: "cienzar",
  players: 2,
};

const clampPlayers = (value) => Math.max(2, Math.min(4, Number(value) || 2));
const clampSandboxZoom = (value) => Math.min(1.08, Math.max(0.76, value));

const getSandboxCopy = (language, key, values = {}) => {
  const normalizedLanguage = normalizeLanguage(language);
  const entry = SANDBOX_COPY[key] || {};
  let copy = entry[normalizedLanguage] || entry.es || entry.en || "";

  Object.entries(values || {}).forEach(([name, value]) => {
    copy = copy.split(`{${name}}`).join(String(value));
  });

  return copy;
};

const normalizeSandboxMode = (value) =>
  SANDBOX_GAME_MODE_OPTIONS.some((option) => option.id === value)
    ? value
    : PLAY_SANDBOX_DEFAULT_PREFERENCES.mode;

const readPlaySandboxPreferences = () => {
  if (typeof window === "undefined") {
    return PLAY_SANDBOX_DEFAULT_PREFERENCES;
  }

  try {
    const rawValue = window.localStorage.getItem(PLAY_SANDBOX_PREFERENCES_STORAGE_KEY);
    if (!rawValue) {
      return PLAY_SANDBOX_DEFAULT_PREFERENCES;
    }

    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object") {
      return PLAY_SANDBOX_DEFAULT_PREFERENCES;
    }

    return {
      mode: normalizeSandboxMode(parsed.mode),
      players: clampPlayers(parsed.players),
    };
  } catch {
    return PLAY_SANDBOX_DEFAULT_PREFERENCES;
  }
};

const writePlaySandboxPreferences = (preferences) => {
  if (typeof window === "undefined") return;
  try {
    const payload = {
      mode: normalizeSandboxMode(preferences?.mode),
      players: clampPlayers(preferences?.players),
    };
    window.localStorage.setItem(PLAY_SANDBOX_PREFERENCES_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage failures.
  }
};

const PlaySandbox = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const language = useInterfaceLanguage(outletContext.language);
  const sandboxText = useCallback(
    (key, values) => getSandboxCopy(language, key, values),
    [language],
  );
  const screenRef = useRef(null);
  const [mode, setMode] = useState(() => readPlaySandboxPreferences().mode);
  const [players, setPlayers] = useState(() => readPlaySandboxPreferences().players);
  const [interfaceZoom, setInterfaceZoom] = useState(1);
  const [startingGame, setStartingGame] = useState(false);

  useEffect(() => {
    const screen = screenRef.current;
    if (!screen) return undefined;

    const handleWheelZoom = (event) => {
      if (!event.ctrlKey) return;

      event.preventDefault();
      setInterfaceZoom((currentZoom) => {
        const nextZoom = currentZoom + (event.deltaY < 0 ? 0.05 : -0.05);
        return Number(clampSandboxZoom(nextZoom).toFixed(2));
      });
    };

    screen.addEventListener("wheel", handleWheelZoom, { passive: false });

    return () => {
      screen.removeEventListener("wheel", handleWheelZoom);
    };
  }, []);

  useEffect(() => {
    writePlaySandboxPreferences({
      mode,
      players,
    });
  }, [mode, players]);

  const startSandboxGame = async () => {
    if (startingGame) return;

    const selectedBaseMode = normalizeSandboxMode(mode);
    const selectedPlayers = clampPlayers(players);

    try {
      setStartingGame(true);
      const response = await axios.post("/tournaments", {
        tournamentName: "",
        mode: "freebox",
        freeBoxBaseMode: selectedBaseMode,
        freeBoxRules: true,
        memberSize: selectedPlayers,
        havingTime: SANDBOX_DEFAULT_TURN_TIME,
        duration: 1,
        startingTime: new Date().toISOString(),
        isQuickMatch: false,
        isIndependentMatch: false,
        hasMatchPassword: false,
        matchPassword: "",
        tournamentPassword: "",
        joinPassword: "",
        tournamentRewardValue: 0,
        tournamentWinnerPrizeValue: 0,
      });

      const created = response?.data || null;
      const tournamentId = String(created?._id || "").trim();

      if (!tournamentId) {
        throw new Error(sandboxText("missingSandboxId"));
      }

      navigate(`/game/${tournamentId}?autoplay=1`, {
        state: {
          fromLobbySource: "sandbox",
          source: "lobbySandboxInterface",
          sandboxPreferences: {
            mode: "freebox",
            freeBoxBaseMode: selectedBaseMode,
            freeBoxRules: true,
            memberSize: selectedPlayers,
            havingTime: SANDBOX_DEFAULT_TURN_TIME,
          },
        },
      });
    } catch (error) {
      console.error("Error starting sandbox game:", error);
      notification.error({
        message: sandboxText("startErrorTitle"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          sandboxText("startErrorDescription"),
      });
    } finally {
      setStartingGame(false);
    }
  };

  const openChampionship = () => {
    navigate("/game/play");
  };

  return (
    <main
      ref={screenRef}
      className="xima-create-game-screen xima-sandbox-game-screen"
      style={{ "--xima-create-zoom": String(interfaceZoom) }}
    >
      <div className="xima-create-game-overlay" aria-hidden="true" />
      <div className="xima-create-game-shell">
        <header className="xima-create-game-header">
          <div className="xima-create-game-logo" aria-label="XIMA">
            XiMA
          </div>
          <button
            type="button"
            onClick={() => navigate("/game/lobby")}
            className="xima-create-back-button"
          >
            <span aria-hidden="true">&lt;</span>
            {sandboxText("backToLobby")}
          </button>
          <div className="xima-create-game-title">
            <h1>{sandboxText("title")}</h1>
            <p>{sandboxText("subtitle")}</p>
          </div>
        </header>

        <div className="xima-create-controls-stage">
          <div className="xima-create-game-hero-space" aria-hidden="true" />

          <section className="xima-create-mode-grid" aria-label={sandboxText("modeSectionLabel")}>
            {SANDBOX_GAME_MODE_OPTIONS.map((option) => {
              const selected = mode === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setMode(option.id)}
                  className={`xima-create-mode-card ${option.className}${
                    selected ? " xima-create-selected" : ""
                  }`}
                  aria-pressed={selected}
                >
                  <span className="xima-create-mode-icon">
                    <img
                      src={option.icon}
                      alt=""
                      aria-hidden="true"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  </span>
                  <span className="xima-create-mode-copy">
                    <strong>{sandboxText(option.titleKey)}</strong>
                    <small>{sandboxText(option.subtitleKey)}</small>
                    <small>{sandboxText(option.detailKey)}</small>
                  </span>
                </button>
              );
            })}
          </section>

          <section className="xima-create-options-grid xima-sandbox-options-grid">
            <div className="xima-create-option-panel">
              <div className="xima-create-panel-heading">
                <strong>{sandboxText("playersTitle")}</strong>
                <span>{sandboxText("playersDescription")}</span>
              </div>
              <div className="xima-create-choice-grid xima-create-player-grid">
                {SANDBOX_PLAYER_OPTIONS.map((option) => {
                  const selected = Number(players) === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setPlayers(option.value)}
                      className={`xima-create-choice-button${selected ? " xima-create-selected" : ""}`}
                      aria-pressed={selected}
                    >
                      <img
                        src={option.icon}
                        alt=""
                        aria-hidden="true"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                      <strong>{sandboxText(option.labelKey)}</strong>
                      <small>{sandboxText(option.detailKey)}</small>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="xima-create-option-panel xima-sandbox-rules-panel">
              <div className="xima-create-panel-heading">
                <strong>{sandboxText("rulesTitle")}</strong>
                <span>{sandboxText("rulesIntro")}</span>
              </div>
              <div className="xima-sandbox-rule-list" aria-label={sandboxText("rulesAriaLabel")}>
                <div className="xima-sandbox-rule-item">
                  <IoCubeOutline aria-hidden="true" />
                  <span>{sandboxText("ruleFreeMovement")}</span>
                </div>
                <div className="xima-sandbox-rule-item">
                  <IoCubeOutline aria-hidden="true" />
                  <span>{sandboxText("ruleRightClick")}</span>
                </div>
                <div className="xima-sandbox-rule-item">
                  <IoCubeOutline aria-hidden="true" />
                  <span>{sandboxText("ruleNoTurnTime")}</span>
                </div>
              </div>
            </div>
          </section>

          <section className="xima-create-action-grid">
            <button
              type="button"
              onClick={() => navigate("/game/store?fromLobbySource=store")}
              className="xima-create-promo-card xima-create-store-card"
            >
              <span className="xima-create-promo-icon" aria-hidden="true">
                <IoGiftOutline />
              </span>
              <span>
                <strong>{sandboxText("goToShop")}</strong>
                <small>{sandboxText("shopDescription")}</small>
              </span>
              <img src="/store/xima-board/xima-board-0.png" alt="" aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={startSandboxGame}
              className="xima-create-submit-button xima-sandbox-submit-button"
              disabled={startingGame}
              aria-busy={startingGame}
            >
              <span aria-hidden="true">
                <IoPlayOutline />
              </span>
              {startingGame ? sandboxText("opening") : sandboxText("play")}
            </button>

            <button
              type="button"
              onClick={openChampionship}
              className="xima-create-promo-card xima-create-championship-card"
            >
              <span className="xima-create-promo-icon" aria-hidden="true">
                <IoRocketOutline />
              </span>
              <span>
                <strong>{sandboxText("championshipTitle")}</strong>
                <small>{sandboxText("championshipDescription")}</small>
              </span>
            </button>
          </section>
        </div>
      </div>
    </main>
  );
};

export default PlaySandbox;
